// Reskin art: images built by tools/build-art.py into public/art. Everything here is optional;
// until an image has loaded (or if it is missing) the old hand-drawn code draws instead.

type Ground = Record<string, { row: number; n: number }>;

const images = new Map<string, HTMLImageElement>();
let ground: Ground | null = null;

function load(name: string): HTMLImageElement | undefined {
  if (typeof Image === 'undefined') return undefined; // tools/draw-smoke.ts runs without a browser
  let img = images.get(name);
  if (!img) {
    img = new Image();
    img.src = `/art/${name}`;
    images.set(name, img);
  }
  return img;
}

export function ready(img: HTMLImageElement | undefined): img is HTMLImageElement {
  return Boolean(img && img.complete && img.naturalWidth > 0);
}

export function initArt(): void {
  load('ground.png');
  initChars();
  initWeapons();
  initObjects();
  initShots();
  initBooms();
  load('water.png');
  load('toxic.png');
  fetch('/art/ground.json')
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (ground = j))
    .catch(() => {});
}

/** Paints a textured ground tile. Returns false when there is no texture for it. */
export function drawGroundTile(ctx: CanvasRenderingContext2D, tile: number, n: number, x: number, y: number, size: number): boolean {
  const spec = ground?.[tile];
  const img = images.get('ground.png');
  if (!spec || !ready(img)) return false;
  const col = spec.n > 1 ? (n >>> 2) % spec.n : 0;
  ctx.drawImage(img, col * 48, spec.row * 48, 48, 48, x, y, size, size);
  return true;
}

// ---- Characters: char-<slug>.png, 8 rows (headings clockwise from east) x 25 cols of 128px.
// col 0 standing (hand-drawn), 1-8 walk, 9-16 attack, 17-24 death. Feet sit at y=115.
const FRAME = 128;
const FEET = 115;
/** World px per sprite px: a 48px tile is 16 world px. */
const K = 1 / 3;
let charList: Set<string> | null = null;
const anim = new Map<string | number, { pose: string; since: number; deadSince: number | null }>();

export function initChars(): void {
  fetch('/art/chars.json')
    .then((r) => (r.ok ? r.json() : []))
    .then((list: string[]) => {
      charList = new Set(list);
      for (const slug of list) load(`char-${slug}.png`);
    })
    .catch(() => {});
}

export function hasChar(slug: string | null | undefined): boolean {
  return Boolean(slug && charList?.has(slug) && ready(images.get(`char-${slug}.png`)));
}

export function heading(aim: number): number {
  return ((Math.round(aim / (Math.PI / 4)) % 8) + 8) % 8;
}

/** Draws an animated character with its feet at (x, y). Returns false if its sheet isn't loaded. */
export function drawChar(
  ctx: CanvasRenderingContext2D,
  slug: string,
  key: string | number,
  x: number,
  y: number,
  aim: number,
  pose: string,
  dead: boolean,
  flash: boolean,
): boolean {
  if (!hasChar(slug)) return false;
  const img = images.get(`char-${slug}.png`)!;
  const now = performance.now() / 1000;
  let st = anim.get(key);
  if (!st) {
    st = { pose, since: now, deadSince: dead ? now - 10 : null };
    anim.set(key, st);
  }
  if (st.pose !== pose) {
    st.pose = pose;
    st.since = now;
  }
  if (dead && st.deadSince === null) st.deadSince = now;
  if (!dead) st.deadSince = null;
  const t = now - st.since;
  let col = 0;
  if (dead) col = 17 + Math.min(7, Math.floor((now - (st.deadSince ?? now)) * 10));
  else if (pose === 'windup') col = 9 + Math.min(2, Math.floor(t * 10));
  else if (pose === 'swing') col = 9 + Math.min(7, 3 + Math.floor(t * 16));
  else if (pose === 'walk') col = 1 + (Math.floor(now * 10 + (typeof key === 'number' ? key * 0.37 : 0)) % 8);
  const row = heading(aim);
  const size = FRAME * K;
  const dx = x - size / 2;
  const dy = y - FEET * K;
  ctx.save();
  // Downscaled sprites (game scale under 3) read better smoothed than nearest-neighbour.
  ctx.imageSmoothingEnabled = ctx.getTransform().a < 3 * (globalThis.devicePixelRatio || 1);
  if (dead && now - (st.deadSince ?? now) > 1.2) ctx.globalAlpha = 0.6;
  ctx.drawImage(img, col * FRAME, row * FRAME, FRAME, FRAME, dx, dy, size, size);
  if (flash) {
    ctx.globalAlpha = 0.75;
    ctx.filter = 'brightness(0) invert(1)';
    ctx.drawImage(img, col * FRAME, row * FRAME, FRAME, FRAME, dx, dy, size, size);
  }
  ctx.restore();
  return true;
}

// ---- Weapons: weapons.png strip, weapons.json { id: { x, w, h, base (deg the art points), len (world px) } }.
type WeaponArt = { x: number; w: number; h: number; base: number; len: number };
let weaponArt: Record<string, WeaponArt> | null = null;

export function initWeapons(): void {
  load('weapons.png');
  fetch('/art/weapons.json')
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (weaponArt = j))
    .catch(() => {});
}

/**
 * Draws the held weapon at the hand, pointing along aim. Melee swings arc and thrust; guns kick back
 * and jiggle while firing. Returns false when there is no art for it (the old stick draws instead).
 */
export function drawWeaponArt(ctx: CanvasRenderingContext2D, id: string, handX: number, handY: number, aim: number, swing: boolean, melee: boolean): boolean {
  const spec = weaponArt?.[id];
  const img = images.get('weapons.png');
  if (!spec || !ready(img)) return false;
  const now = performance.now() / 1000;
  const left = Math.cos(aim) < 0;
  let angle = aim;
  let push = 0;
  if (swing && melee) {
    angle += Math.sin(now * 16) * 0.75;
    push = 3 + Math.sin(now * 16) * 2;
  } else if (swing) {
    push = -2 - Math.abs(Math.sin(now * 45)) * 2;
    angle += Math.sin(now * 53) * 0.08;
  }
  const base = (spec.base * Math.PI) / 180;
  // Scale so the art's long axis (along base) is len world px.
  const along = Math.abs(spec.w * Math.cos(base)) + Math.abs(spec.h * Math.sin(base));
  const k = spec.len / Math.max(1, along);
  ctx.save();
  ctx.imageSmoothingEnabled = true;
  ctx.translate(handX + Math.cos(angle) * push, handY + Math.sin(angle) * push);
  ctx.rotate(angle);
  if (left) ctx.scale(1, -1);
  ctx.rotate(-base);
  // Grip: the back end of the art, a little in from the edge.
  const gx = spec.base === 0 ? spec.w * 0.3 : spec.base === -90 ? spec.w * 0.5 : spec.w * 0.18;
  const gy = spec.base === 0 ? spec.h * 0.55 : spec.base === -90 ? spec.h * 0.85 : spec.h * 0.82;
  ctx.drawImage(img, spec.x, 0, spec.w, spec.h, -gx * k, -gy * k, spec.w * k, spec.h * k);
  ctx.restore();
  return true;
}

// ---- World objects: objects.png strip, objects.json { group: [{ x, w, h }, ...variants] }.
let objectArt: Record<string, { x: number; w: number; h: number }[]> | null = null;

export function initObjects(): void {
  load('objects.png');
  fetch('/art/objects.json')
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (objectArt = j))
    .catch(() => {});
}

/**
 * Draws a world object standing with its bottom centre at (x, foot). variant picks among the group's
 * pictures (any number; it wraps). dim darkens it (opened crates, emptied barrels).
 */
export function drawObject(ctx: CanvasRenderingContext2D, group: string, variant: number, x: number, foot: number, dim = false, flip = false): boolean {
  const list = objectArt?.[group];
  const img = images.get('objects.png');
  if (!list || !list.length || !ready(img)) return false;
  const o = list[((variant % list.length) + list.length) % list.length];
  const w = o.w * K;
  const h = o.h * K;
  ctx.save();
  ctx.imageSmoothingEnabled = ctx.getTransform().a < 3 * (globalThis.devicePixelRatio || 1);
  if (dim) ctx.filter = 'brightness(0.6) saturate(0.6)';
  if (flip) {
    ctx.translate(x, 0);
    ctx.scale(-1, 1);
    ctx.translate(-x, 0);
  }
  ctx.drawImage(img, o.x, 0, o.w, o.h, x - w / 2, foot - h, w, h);
  ctx.restore();
  return true;
}

// ---- Liquids: water.png / toxic.png are seamless 96px swatches, drawn as patterns in world space
// (one swatch spans two tiles) with two layers drifting apart, so pools ripple without tile seams.
const liquidPatterns = new Map<string, CanvasPattern | null>();

function liquidPattern(ctx: CanvasRenderingContext2D, name: string): CanvasPattern | null {
  if (liquidPatterns.has(name)) return liquidPatterns.get(name)!;
  const img = load(`${name}.png`);
  if (!ready(img)) return null;
  const pat = ctx.createPattern(img, 'repeat');
  liquidPatterns.set(name, pat);
  return pat;
}

export function drawLiquid(ctx: CanvasRenderingContext2D, name: 'water' | 'toxic', x: number, y: number, size: number, time: number): boolean {
  const pat = liquidPattern(ctx, name);
  if (!pat) return false;
  const slow = name === 'water' ? 2.2 : 1.2;
  ctx.save();
  ctx.fillStyle = pat;
  pat.setTransform(new DOMMatrix().translate(time * slow, time * slow * 0.4).scale(K));
  ctx.fillRect(x, y, size, size);
  ctx.globalAlpha = 0.35;
  pat.setTransform(new DOMMatrix().translate(-time * slow * 0.7 + 13, time * slow * 0.6 + 29).scale(K));
  ctx.fillRect(x, y, size, size);
  ctx.restore();
  return true;
}

// ---- Shots: shots.png, one row of 16px frames per look (all pointing right); shots.json
// { look: { row, n, fps, ax, ay, rgb } }, (ax, ay) = the bright head, drawn on the shot's position.
type ShotArt = { row: number; n: number; fps: number; ax: number; ay: number; rgb: [number, number, number] };
let shotArt: Record<string, ShotArt> | null = null;

export function initShots(): void {
  load('shots.png');
  fetch('/art/shots.json')
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (shotArt = j))
    .catch(() => {});
}

/** Average colour of a look, for its night glow. */
export function shotRgb(look: string): [number, number, number] | null {
  return shotArt?.[look]?.rgb ?? null;
}

/** Draws an animated projectile flying along (vx, vy), k world px per art px. False if not loaded. */
export function drawShotArt(ctx: CanvasRenderingContext2D, look: string, id: number, x: number, y: number, vx: number, vy: number, k: number): boolean {
  const spec = shotArt?.[look];
  const img = images.get('shots.png');
  if (!spec || !ready(img)) return false;
  const frame = (Math.floor((performance.now() / 1000) * spec.fps) + id * 7) % spec.n;
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  ctx.translate(x, y);
  ctx.rotate(Math.atan2(vy, vx));
  ctx.drawImage(img, frame * 16, spec.row * 16, 16, 16, -spec.ax * k, -spec.ay * k, 16 * k, 16 * k);
  ctx.restore();
  return true;
}

// ---- Explosions: explosions.png, explosions.json { look: { y, size, n, fps, r } }. The server only
// keeps a blast for a fraction of a second, so each one plays out here on its own clock from the
// first snapshot that showed it.
type BoomArt = { y: number; size: number; n: number; fps: number; r: number };
type Boom = { look: string; x: number; y: number; r: number; t0: number; reverse: boolean };

let boomArt: Record<string, BoomArt> | null = null;
const booms = new Map<number, Boom>();

export function initBooms(): void {
  load('explosions.png');
  fetch('/art/explosions.json')
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => (boomArt = j))
    .catch(() => {});
}

/** Starts a blast for key `id` (ignored if already started). r = radius in world px; delay in s. */
export function noteBoom(id: number, look: string, x: number, y: number, r: number, reverse = false, delay = 0): void {
  if (booms.has(id) || !boomArt?.[look]) return;
  booms.set(id, { look, x, y, r, t0: performance.now() / 1000 + delay, reverse });
}

/** A boss going up: one big blast, then smaller pops scattered around it over half a second. */
export function noteBossBoom(id: number, x: number, y: number, r: number): void {
  if (booms.has(id)) return;
  noteBoom(id, 'big', x, y - 6, r * 2.2);
  for (let i = 0; i < 5; i++) {
    const a = id * 2.4 + i * 2.1;
    const d = r * (0.8 + (i % 3) * 0.35);
    noteBoom(-(id * 8 + i + 1), 'small', x + Math.cos(a) * d, y - 4 + Math.sin(a) * d * 0.7, r * (0.7 + (i % 2) * 0.3), false, 0.12 + i * 0.11);
  }
}

function boomT(b: Boom, now: number): { spec: BoomArt; frame: number; t: number } | null {
  const spec = boomArt?.[b.look];
  if (!spec) return null;
  const t = (now - b.t0) / (spec.n / spec.fps);
  const frame = Math.floor(t * spec.n);
  return { spec, frame: b.reverse ? spec.n - 1 - frame : frame, t };
}

export function drawBooms(ctx: CanvasRenderingContext2D): void {
  const img = images.get('explosions.png');
  const now = performance.now() / 1000;
  for (const [id, b] of booms) {
    const at = boomT(b, now);
    // Finished entries linger a little so a late snapshot of the same fx doesn't restart them.
    if (!at || at.t > 3) {
      booms.delete(id);
      continue;
    }
    if (at.t < 0 || at.t >= 1 || !ready(img)) continue;
    const k = b.r / at.spec.r;
    const size = at.spec.size * k;
    ctx.save();
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(img, at.frame * at.spec.size, at.spec.y, at.spec.size, at.spec.size, b.x - size / 2, b.y - size / 2, size, size);
    ctx.restore();
  }
}

/** Live blasts as night lights: a flash that fades over the first half of the animation. */
export function boomLights(): { x: number; y: number; r: number; look: string; fade: number }[] {
  const now = performance.now() / 1000;
  const out: { x: number; y: number; r: number; look: string; fade: number }[] = [];
  for (const b of booms.values()) {
    const at = boomT(b, now);
    if (at && at.t >= 0 && at.t < 0.6) out.push({ x: b.x, y: b.y, r: b.r, look: b.look, fade: 1 - at.t / 0.6 });
  }
  return out;
}
